import { Outlet, createFileRoute, notFound } from '@tanstack/react-router';

import { labelCollection } from '@/api/collections/labels/label';
import { labelMapCollection } from '@/api/collections/labels/label-map';
import { useLatestBedrijf } from '@/api/queries/bedrijf';
import { useBesluitId } from '@/api/queries/besluit';
import { useBesluitType, useBesluitTypeId } from '@/api/queries/besluitType';
import { Button } from '@/components/Button/Button';
import { Disclaimer } from '@/components/Disclaimer/Disclaimer';
import { DocumentTitle } from '@/components/DocumentTitle';
import { ModalTrigger } from '@/components/Modal';
import { Navbar } from '@/components/Navbar/Navbar';
import { TabsPanel } from '@/components/TabsPanel/TabsPanel';
import { eStrings } from '@/constants/strings';
import { SolidFilter, SolidList } from '@/icons/components';
import { SelectBesluit } from '@/modals/SelectBesluit';

import { Body } from '../-components/Body';
import { Header } from '../-components/Header';
import useViewerStore, { SidebarLeftTab } from '../-utils/useViewerStore';
import { BesluitSidebarFilter } from './-components/BesluitSidebarFilter';
import { BesluitSidebarIndex } from './-components/BesluitSidebarIndex';
import { useViewerBesluitGrondslagen } from './-utils/useViewerBesluitGrondslagen';

/**
 * Route
 */
export const Route = createFileRoute(
  '/$bedrijfLineageId/viewer/besluit/$besluitId/_layout'
)({
  loader: async ({
    params,
    context: { queryClient, voorschriftLabelCollection },
  }) => {
    const [latestBedrijf, besluit] = await Promise.all([
      queryClient.ensureQueryData(
        useLatestBedrijf.getOptions(params.bedrijfLineageId)
      ),
      queryClient.ensureQueryData(useBesluitId.getOptions(params.besluitId)),
      queryClient.ensureQueryData(useBesluitType.getOptions({})),
      queryClient.ensureQueryData(
        useViewerBesluitGrondslagen.getOptions({ besluitId: params.besluitId })
      ),
      voorschriftLabelCollection.preload(),
      labelMapCollection.preload(),
      labelCollection.preload(),
    ]);

    if (!latestBedrijf || !besluit) throw notFound();
  },
  component: BesluitIdComponent,
  onLeave: () => {
    useViewerStore.getState().setShowAllMeta(false);
  },
});

/**
 * Route component
 */
function BesluitIdComponent() {
  const { bedrijfLineageId, besluitId } = Route.useParams();

  const bedrijf = useLatestBedrijf(bedrijfLineageId);
  const besluit = useBesluitId(besluitId, {
    // After a besluit is published, it will never change again.
    staleTime: Infinity,
  });
  const besluitType = useBesluitTypeId(besluit.data?.Type ?? '', {
    enabled: !!besluit.data?.Type,
  });

  const sidebarLeftTabId = useViewerStore((state) => state.sidebarLeftTabId);
  const setSidebarLeftTabId = useViewerStore(
    (state) => state.setSidebarLeftTabId
  );

  if (!bedrijf.data || !besluit.data) return null;

  return (
    <>
      <DocumentTitle
        sections={[
          eStrings.LBL_DIGI_V,
          bedrijf.data.Naam ?? '',
          `${besluit.data.Zaaknummer} (${besluitType.data?.Naam})`,
        ]}
      />

      <Navbar />

      <Header bedrijf={bedrijf.data} mode="besluit">
        <ModalTrigger
          modalComponent={SelectBesluit}
          onOpen={() => ({
            bedrijf: bedrijf.data!,
            activeBesluitId: besluitId,
          })}
          asChild
        >
          <Button className="w-[180px] justify-center">Kies een besluit</Button>
        </ModalTrigger>
      </Header>

      <Body>
        <Body.Sidebar ariaLabel="Besluit navigatie">
          <TabsPanel
            value={sidebarLeftTabId}
            onValueChange={(value) =>
              setSidebarLeftTabId(value as SidebarLeftTab)
            }
          >
            <TabsPanel.Header>
              <TabsPanel.Trigger
                className="h-7"
                value={SidebarLeftTab.INDEX}
                label="Inhoud"
                icon={SolidList}
              />
              <TabsPanel.Trigger
                className="h-7"
                value={SidebarLeftTab.FILTER}
                label="Filteren"
                icon={SolidFilter}
              />
            </TabsPanel.Header>

            <TabsPanel.Content value={SidebarLeftTab.INDEX}>
              <BesluitSidebarIndex besluit={besluit.data} />
            </TabsPanel.Content>

            <TabsPanel.Content value={SidebarLeftTab.FILTER}>
              <BesluitSidebarFilter
                besluitId={besluitId}
                bedrijfLineageId={bedrijfLineageId}
              />
            </TabsPanel.Content>
          </TabsPanel>
        </Body.Sidebar>

        <Body.Content className="pb-10">
          <Outlet />
          <Disclaimer className="mt-2 px-6" />
        </Body.Content>
      </Body>
    </>
  );
}
